import React, { useState, useMemo, useCallback } from "react";
import { IAgenticInlineWidgetPopoverProps, WidgetType } from "../../models/interfaces";
import { useTranslation } from "react-i18next";
import DynamicCharts from "src/components/Charts/DynamicCharts";
import { transformChartDataWithColor } from "src/pages/Insights/transformer";
import { chartType } from "src/pages/Insights/models/interfaces";
import "./style.css";

const CHART_CONSTANTS = {
	BAR_SIZE: 18,
	BAR_GAP: 10,
	AXIS_HEIGHT: 40,
	MIN_HEIGHT: 150,
	MAX_Y_AXIS_WIDTH: 380,
	MIN_Y_AXIS_WIDTH: 100,
	CHAR_WIDTH_MULTIPLIER: 5,
} as const;

const AgenticInlineWidgetPopover: React.FC<IAgenticInlineWidgetPopoverProps> = ({ TopDataIntoChartFormat, onViewDetails, widgetData }) => {
	const { t } = useTranslation();
	const [currentIndex, setCurrentIndex] = useState(0);
	const currentRef = useMemo(() => widgetData[currentIndex], [widgetData, currentIndex]);
	const total = useMemo(() => widgetData?.length, [widgetData?.length]);
	const studyName = currentRef?.tool_tip?.study_name || "";
	const widgetId = currentRef?.widgetId || "";

	const handleNext = useCallback(() => {
		if (currentIndex < total - 1) setCurrentIndex(currentIndex + 1);
	}, [currentIndex, total, setCurrentIndex]);

	const handlePrev = useCallback(() => {
		if (currentIndex > 0) setCurrentIndex(currentIndex - 1);
	}, [currentIndex, setCurrentIndex]);

	const handleOpenDrawer = useCallback(() => {
		onViewDetails?.(currentRef);
	}, [onViewDetails, currentRef]);

	// Generate chart data for CURRENT widget (updates when currentIndex changes)
	const currentChartData = useMemo(() => {
		return widgetId ? TopDataIntoChartFormat[widgetId] : [];
	}, [TopDataIntoChartFormat, widgetId]);

	const chartHeight: number = useMemo(() => {
		const dataCount = currentChartData?.length || 0;
		const barSize = CHART_CONSTANTS?.BAR_SIZE; // From config
		const barGap = CHART_CONSTANTS?.BAR_GAP; // Approximate gap between bars
		const axisHeight = CHART_CONSTANTS?.AXIS_HEIGHT; // Space for x-axis

		// Calculate total height for all bars
		const totalHeight = dataCount * (barSize + barGap) + axisHeight;

		return Math.max(CHART_CONSTANTS?.MIN_HEIGHT, totalHeight);
	}, [currentChartData?.length]);

	// Extract selected metadata from currentRef. Older chats may not have this
	// field, so invalid or empty entries are ignored.
	const selectedMetadataChips = useMemo(() => {
		const selectedFilters = currentRef?.tool_tip?.selected_metadata_filters;

		if (!selectedFilters || !Array.isArray(selectedFilters) || selectedFilters.length === 0) {
			return [];
		}

		const chips: { label: string; value: string }[] = [];
		for (const filter of selectedFilters) {
			const label = filter?.tag_category || "";
			const options = Array.isArray(filter?.options) ? filter.options : [];
			if (!label) continue;
			for (const option of options) {
				const value = option?.tag_value || "";
				if (!value) continue;
				chips.push({ label, value });
			}
		}
		return chips;
	}, [currentRef]);
	const visibleMetadataChips = selectedMetadataChips.slice(0, 2);
	const hiddenMetadataCount = Math.max(selectedMetadataChips.length - visibleMetadataChips.length, 0);
	const hiddenMetadataTitle = selectedMetadataChips
		.slice(2)
		.map(({ label, value }) => `${label}: ${value}`)
		.join(", ");

	if (!currentRef) return null;

	return (
		<div className="shadow-xl rounded-xl overflow-hidden Widget-popover-container">
			{/* Sticky Header - All in one row */}
			<div className="flex items-center justify-between mb-2">
				<div className={`flex items-center gap-1`}>
					<div className="flex items-center gap-1 flex-1">
						<span className="text-base-1 text-[--secondary-color] font-semibold truncate">
							{currentRef.widget_type === WidgetType?.Deepdive
								? `${t("Counts")} ${t("Preview")}`
								: currentRef.widget_type === WidgetType.Counts
								? `${t("Counts")} ${t("Preview")} `
								: currentRef.widget_type === WidgetType.NotesCounts || currentRef.widget_type === "notes_counts"
								? `${t("Notes Counts")} ${t("Preview")}`
								: ""}
						</span>
					</div>
				</div>
				{/* Right: Navigation arrows */}
				<div className="flex items-center gap-0.5 ml-2">
					{total > 1 && (
						<>
							<button
								onClick={handlePrev}
								disabled={currentIndex === 0}
								className={`widget-circle-arrow flex items-center
                            ${currentIndex === 0 ? "cursor-not-allowed" : "cursor-pointer"}
                          `}>
								<i className={`material-symbols-outlined text-lg ${currentIndex === 0 ? "text-[--neutral-grey]" : "text-[--secondary-color]"}`}>chevron_backward</i>
							</button>
							<span className="page-number px-1">
								<span className="text-[--secondary-color]">{currentIndex + 1}</span>
								<span>/{total}</span>
							</span>

							<button
								onClick={handleNext}
								disabled={currentIndex === total - 1}
								className={`widget-circle-arrow flex items-center
                            ${currentIndex === total - 1 ? "cursor-not-allowed" : "cursor-pointer"}
                          `}>
								<i className={`material-symbols-outlined text-lg ${currentIndex === total - 1 ? "text-[--neutral-grey]" : "text-[--secondary-color]"}`}>
									chevron_forward
								</i>
							</button>
						</>
					)}
				</div>
			</div>

			<div className="widget-query-text truncate-text-1-lines font-medium text-[--neutral-grey] mb-2">{currentRef.tool_tip?.query}</div>
			<div className="widget-study-name text-[--secondary-color-2] font-normal flex justify-start mb-2">
				<span title={studyName} className="underline truncate">
					{studyName}
				</span>
				<span className="italic ml-1 whitespace-nowrap">
					{currentRef.widget_type === WidgetType.NotesCounts || currentRef.widget_type === "notes_counts" ? t("(Notes)") : t("(Qualitative)")}
				</span>
			</div>

			<div className="widget-chart-scroll-container">
				<div className="widget-chart-wrapper">
					<DynamicCharts
						obj={transformChartDataWithColor(currentChartData || [])}
						isMini={true}
						type={chartType.MultiColor_Histogram_Chart}
						config={{
							type: chartType.MultiColor_Histogram_Chart,
							showCartesianGrid: false,
							showLegends: false,
							dataKeyXAxis: "label",
							XAxisType: "number",
							XAxisHide: false,
							YAxisType: "category",
							YAxisHide: false,
							layout: "horizontal",
							barSize: 18,
							showLabels: true,
							showTooltip: false,
							width: "60%",
							height: chartHeight,
							xOrientation: "bottom",
							barCategoryGap: "10%",
							yLabelsWidth: 150,
							yAisFontSize: 8,
							maxYaxisChar: 22,
						}}
					/>
				</div>
			</div>

			{/* Footer with  View Details */}
			<div className="widget-popover-footer">
				<div className="widget-filter-section">
					{visibleMetadataChips.length > 0 && (
						<>
							<i className="material-symbols-outlined filter-icon">filter_alt</i>
							{visibleMetadataChips.map(({ label, value }) => (
								<div key={`${label}:${value}`} className="filter-label-value-wrapper" title={`${label}: ${value}`}>
									<span className="filter-label">{label}:</span>
									<span className="filter-value">{value}</span>
								</div>
							))}
							{hiddenMetadataCount > 0 && (
								<span className="filter-more" title={hiddenMetadataTitle}>
									..
								</span>
							)}
						</>
					)}
				</div>
				<div onClick={handleOpenDrawer} className="widget-view-details-text font-semibold cursor-pointer text-[x-small] underline text-[--primary-color] flex justify-end">
					{`${t("View Details")}`}
				</div>
			</div>
		</div>
	);
};

export default AgenticInlineWidgetPopover;
